import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Avatar,
  Autocomplete,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import ExpandableText from "./ExpandableText";
import {
  timerService,
  formatDuration,
  isManagerRole,
} from "../services/timerService";

// ── design tokens — rich but softened palette ───────────────────────────────
const INK = "#1F2430";
const SUB = "#6B7280";
const MUTED = "#9AA2B1";
const LINE = "#ECEEF2";
const BRAND = "#3E5AA6"; // softened royal blue
const BRAND2 = "#5B78C4";
const CARD = {
  bgcolor: "#fff",
  border: `1px solid ${LINE}`,
  borderRadius: 3,
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
};

// softened, slightly desaturated avatar palette
const AVATAR_PALETTE = ["#4560A6", "#5A7BC0", "#3E8B9B", "#4E9B6E", "#B98A4E", "#9270B8", "#B36A90", "#4E9188"];
const avatarColor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const StatTile = ({ icon, label, value, color }) => (
  <Box sx={{ ...CARD, flex: "1 1 150px", minWidth: 140, p: 1.75, display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${color}, ${color}d0)`,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: `0 3px 9px ${color}26`,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 21, fontWeight: 800, color: INK, lineHeight: 1.05, letterSpacing: "-0.4px" }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: SUB, fontWeight: 500 }}>{label}</Typography>
    </Box>
  </Box>
);

const EmptyState = ({ text }) => (
  <Box sx={{ ...CARD, textAlign: "center", py: 6, px: 2, color: MUTED }}>
    <InboxRoundedIcon sx={{ fontSize: 38, color: "#D3D8E0" }} />
    <Typography sx={{ mt: 1, fontSize: 13.5 }}>{text}</Typography>
  </Box>
);

const Timesheet = ({ embedded = false }) => {
  const { userInfo, role, allUsers } = useContext(AutoTaskAuthContext) || {};
  const manager = isManagerRole(role || userInfo?.role);

  const [tab, setTab] = useState(0); // 0 = My, 1 = Team
  const [loading, setLoading] = useState(false);
  const [myRows, setMyRows] = useState([]);
  const [teamRows, setTeamRows] = useState([]);
  const [date, setDate] = useState(todayStr());
  // Whose day the Team tab is showing. null = the whole team.
  const [member, setMember] = useState(null);

  // ── My timesheet (last 30 days) ──
  useEffect(() => {
    if (tab !== 0 || !userInfo?.id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await timerService.userTimesheet(userInfo.id);
        if (!cancelled) setMyRows(Array.isArray(resp?.data) ? resp.data : []);
      } catch (e) {
        if (!cancelled) setMyRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, userInfo?.id]);

  // ── Team timesheet (selected day) ──
  useEffect(() => {
    if (tab !== 1 || !manager || userInfo?.store_id === undefined) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await timerService.storeTimesheet(userInfo.store_id, { date });
        if (!cancelled) setTeamRows(Array.isArray(resp?.data) ? resp.data : []);
      } catch (e) {
        if (!cancelled) setTeamRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, manager, userInfo?.store_id, date]);

  // group My rows by day
  const myByDay = useMemo(() => {
    const map = new Map();
    for (const r of myRows) {
      if (!map.has(r.work_date)) map.set(r.work_date, []);
      map.get(r.work_date).push(r);
    }
    return Array.from(map.entries()).map(([day, rows]) => ({
      day,
      rows,
      totalSeconds: rows.reduce((s, r) => s + Number(r.total_seconds || 0), 0),
    }));
  }, [myRows]);

  // The rows the Team tab is actually showing — everyone, or one person.
  //
  // Filtering here rather than re-fetching per member: the store timesheet for
  // a day is one request that already contains every row, so narrowing it is
  // free and instant, and switching between people costs nothing.
  const shownTeamRows = useMemo(
    () =>
      member
        ? teamRows.filter((r) => Number(r.user_id) === Number(member.id))
        : teamRows,
    [teamRows, member],
  );

  // group Team rows by user
  const teamByUser = useMemo(() => {
    const map = new Map();
    for (const r of shownTeamRows) {
      const key = r.user_id;
      if (!map.has(key)) map.set(key, { id: key, name: r.user_name, rows: [] });
      map.get(key).rows.push(r);
    }
    return Array.from(map.values()).map((u) => ({
      ...u,
      totalSeconds: u.rows.reduce((s, r) => s + Number(r.total_seconds || 0), 0),
    }));
  }, [shownTeamRows]);

  /**
   * Who the member picker offers.
   *
   * The roster, not the rows: somebody who logged nothing today is exactly the
   * person a head goes looking for, and a list built from the timesheet would
   * be missing them. Anyone in the day's rows who is no longer on the roster
   * is added back, so an old row never becomes unreachable.
   */
  const memberOptions = useMemo(() => {
    const map = new Map();
    (Array.isArray(allUsers) ? allUsers : [])
      .filter((u) => String(u?.status ?? "active").toLowerCase() === "active")
      .forEach((u) => map.set(Number(u.id), { id: Number(u.id), name: u.name || "—" }));
    teamRows.forEach((r) => {
      if (!map.has(Number(r.user_id)))
        map.set(Number(r.user_id), {
          id: Number(r.user_id),
          name: r.user_name || "—",
        });
    });
    const logged = new Map();
    teamRows.forEach((r) =>
      logged.set(
        Number(r.user_id),
        (logged.get(Number(r.user_id)) || 0) + Number(r.total_seconds || 0),
      ),
    );
    return Array.from(map.values())
      .map((u) => ({ ...u, seconds: logged.get(u.id) || 0 }))
      .sort((a, b) => b.seconds - a.seconds || a.name.localeCompare(b.name));
  }, [allUsers, teamRows]);

  // ── UI-only derived summaries ──
  const mySummary = useMemo(() => ({
    totalSeconds: myRows.reduce((s, r) => s + Number(r.total_seconds || 0), 0),
    tickets: new Set(myRows.map((r) => r.ticket_id)).size,
    days: new Set(myRows.map((r) => r.work_date)).size,
  }), [myRows]);
  const teamSummary = useMemo(() => ({
    totalSeconds: shownTeamRows.reduce((s, r) => s + Number(r.total_seconds || 0), 0),
    people: new Set(shownTeamRows.map((r) => r.user_id)).size,
    tickets: new Set(shownTeamRows.map((r) => r.ticket_id)).size,
  }), [shownTeamRows]);

  const fmtDayParts = (d) => {
    if (!d) return { dow: "", date: "" };
    const dt = new Date(d);
    return {
      dow: dt.toLocaleDateString("en-GB", { weekday: "short" }),
      date: dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    };
  };

  const TabButton = ({ index, label }) => {
    const activeSel = tab === index;
    return (
      <Box
        onClick={() => setTab(index)}
        sx={{
          px: 2.25,
          py: 0.85,
          borderRadius: 2,
          cursor: "pointer",
          fontSize: 13.5,
          fontWeight: 700,
          color: activeSel ? "#fff" : SUB,
          background: activeSel ? `linear-gradient(135deg, ${BRAND}, ${BRAND2})` : "transparent",
          boxShadow: activeSel ? "0 3px 10px rgba(62,90,166,0.22)" : "none",
          transition: "all 0.2s ease",
          userSelect: "none",
        }}
      >
        {label}
      </Box>
    );
  };

  const GroupCard = ({ leading, subtitle, title, total, rows, keyPrefix }) => (
    <Box sx={{ ...CARD, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25, borderBottom: `1px solid ${LINE}`, bgcolor: "#FCFCFE" }}>
        {leading}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: INK }} noWrap>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 11, color: MUTED }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.5, borderRadius: 999, bgcolor: "#EEF2FB", color: BRAND }}>
          <TimerRoundedIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{formatDuration(total)}</Typography>
        </Box>
      </Box>
      {rows.map((r, i) => (
        <Box
          key={`${keyPrefix}-${r.ticket_id}`}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            px: 2,
            py: 1.15,
            gap: 2,
            borderTop: i === 0 ? "none" : "1px solid #F5F6F9",
            transition: "background 0.15s ease",
            "&:hover": { bgcolor: "#FAFBFE" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0, flex: 1 }}>
            <Box sx={{ px: 1, py: 0.35, borderRadius: 1.5, bgcolor: "#F1F3F7", flexShrink: 0, mt: "1px" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#5A6474", fontFamily: "monospace" }}>
                #{r.ticket_number}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <ExpandableText text={r.description} sx={{ fontSize: 13, color: "#3A4150" }} />

              {/* Which store's ticket this is. A timesheet spans whatever a
                  person worked on, and for Kumudu staff that is routinely three
                  or four different stores in one day — without this the log is a
                  list of ticket numbers with no idea whose work they were. */}
              {r.ticket_store_name && (
                <Typography
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.35,
                    fontSize: 10.5,
                    color: MUTED,
                    mt: 0.3,
                  }}
                >
                  <StorefrontRoundedIcon sx={{ fontSize: 11 }} />
                  {r.ticket_store_name}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{formatDuration(r.total_seconds)}</Typography>
            {r.sessions != null && (
              <Typography sx={{ fontSize: 10.5, color: MUTED }}>
                {r.sessions} session{Number(r.sessions) !== 1 ? "s" : ""}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: embedded ? "none" : 940,
        mx: embedded ? 0 : "auto",
        py: embedded ? 0 : 2.5,
        px: embedded ? 0 : { xs: 0.5, sm: 1 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(62,90,166,0.25)",
          }}
        >
          <AccessTimeRoundedIcon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 21, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.1 }}>
            Timesheet
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: SUB }}>Auto-generated from start / stop work timers</Typography>
        </Box>
      </Box>

      {/* Segmented tabs + (Team) date picker on the same row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "inline-flex", gap: 0.5, p: 0.5, borderRadius: 2.5, bgcolor: "#EFF1F5" }}>
          <TabButton index={0} label="My Timesheet" />
          {manager && <TabButton index={1} label="Team" />}
        </Box>
        {tab === 1 && manager && (
          <TextField
            type="date"
            size="small"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
          />
        )}
        {/* One person's day, by name.
            A team of thirty is thirty cards to scroll before you reach the one
            you were asked about; typing three letters is the whole feature. */}
        {tab === 1 && manager && (
          <Autocomplete
            size="small"
            options={memberOptions}
            value={member}
            onChange={(e, next) => setMember(next)}
            getOptionLabel={(o) => o?.name || ""}
            isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
            sx={{ minWidth: 230, flex: "0 1 260px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Team member"
                placeholder="Everyone"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
              >
                <Avatar
                  sx={{
                    bgcolor: avatarColor(option.name),
                    width: 26,
                    height: 26,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {initials(option.name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: INK }} noWrap>
                    {option.name}
                  </Typography>
                  {/* What they logged on the chosen day, so the list is worth
                      reading before you pick from it. */}
                  <Typography sx={{ fontSize: 10.5, color: MUTED }}>
                    {option.seconds > 0
                      ? formatDuration(option.seconds)
                      : "no time logged"}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        )}
      </Box>

      {/* Summary tiles */}
      {tab === 0 ? (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
          <StatTile icon={<TimerRoundedIcon />} label="Total logged" value={formatDuration(mySummary.totalSeconds)} color={BRAND} />
          <StatTile icon={<ConfirmationNumberRoundedIcon />} label="Tickets" value={mySummary.tickets} color="#3E8B9B" />
          <StatTile icon={<CalendarMonthRoundedIcon />} label="Active days" value={mySummary.days} color="#B98A4E" />
        </Box>
      ) : (
        manager && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
            <StatTile icon={<TimerRoundedIcon />} label="Team total" value={formatDuration(teamSummary.totalSeconds)} color={BRAND} />
            <StatTile icon={<Groups2RoundedIcon />} label="People" value={teamSummary.people} color="#4E9B6E" />
            <StatTile icon={<ConfirmationNumberRoundedIcon />} label="Tickets" value={teamSummary.tickets} color="#3E8B9B" />
          </Box>
        )
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: BRAND }} />
        </Box>
      )}

      {/* MY */}
      {!loading && tab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
          {myByDay.length === 0 && <EmptyState text="No time logged in the last 30 days." />}
          {myByDay.map(({ day, rows, totalSeconds }) => {
            const p = fmtDayParts(day);
            return (
              <GroupCard
                key={day}
                keyPrefix={day}
                title={p.date}
                subtitle={p.dow}
                total={totalSeconds}
                rows={rows}
                leading={
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "#EEF2FB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: BRAND }} />
                  </Box>
                }
              />
            );
          })}
        </Box>
      )}

      {/* TEAM */}
      {!loading && tab === 1 && manager && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
          {teamByUser.length === 0 && (
            <EmptyState
              text={
                member
                  ? `${member.name} logged no time on this day.`
                  : "No time logged by the team on this day."
              }
            />
          )}
          {teamByUser.map((u) => (
            <GroupCard
              key={u.name}
              keyPrefix={u.name}
              title={u.name}
              subtitle={`${u.rows.length} ticket${u.rows.length !== 1 ? "s" : ""}`}
              total={u.totalSeconds}
              rows={u.rows}
              leading={
                <Avatar sx={{ bgcolor: avatarColor(u.name), width: 40, height: 40, fontSize: 15, fontWeight: 700 }}>
                  {initials(u.name)}
                </Avatar>
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Timesheet;
